import React, { useState, useRef, useEffect } from 'react';

export interface JungArchetypeData {
  name: string;
  angle: number; // Angolo centrale in gradi
  radius: number; // Raggio normalizzato (0-1)
  color: string;
  description: string;
}

// Mappa degli archetipi di Jung basata sull'immagine reale fornita dall'utente
// Sequenza esatta partendo dall'alto (0°) in senso orario ogni 30°:
// mago, eroe, uomo comune, festaiolo, amante, angelo amico, re, creatore, idealista, saggio, esploratore, ribelle
export const JUNG_ARCHETYPES: JungArchetypeData[] = [
  // Partendo dall'alto (ore 12) e procedendo in senso orario
  { name: 'Mago', angle: 0, radius: 0.75, color: '#F5A623', description: 'Potere, visionarietà' },
  { name: 'Eroe', angle: 30, radius: 0.75, color: '#FF8C00', description: 'Coraggio, forza' },
  { name: 'Uomo comune', angle: 60, radius: 0.75, color: '#E89B8E', description: 'Senso di appartenenza' },
  
  { name: 'Festaiolo, gaudente', angle: 90, radius: 0.75, color: '#DC6B5C', description: 'Divertimento, umorismo' },
  { name: 'Amante', angle: 120, radius: 0.75, color: '#E74C3C', description: 'Intimità, passione' },
  { name: 'Angelo amico', angle: 150, radius: 0.75, color: '#87CEEB', description: 'Al servizio, solidarietà' },
  
  { name: 'Re, padre', angle: 180, radius: 0.75, color: '#4682B4', description: 'Controllo, regole' },
  { name: 'Creatore', angle: 210, radius: 0.75, color: '#5BA3E8', description: 'Innovativo, artistico' },
  { name: 'Idealista, innocente', angle: 240, radius: 0.75, color: '#A0D4A0', description: 'Ama i valori, felicità' },
  
  { name: 'Saggio', angle: 270, radius: 0.75, color: '#90C890', description: 'Conoscenza comprensione' },
  { name: 'Esploratore', angle: 300, radius: 0.75, color: '#8FBC8F', description: 'Scoperta, nuove esperienze' },
  { name: 'Ribelle', angle: 330, radius: 0.75, color: '#F4A460', description: 'Autonomia dalle convenzioni' }
];

export function resolveJungArchetype(label: string | null | undefined): JungArchetypeData | null {
  const normalized = String(label || '').trim().toLowerCase();
  if (!normalized) return null;
  return JUNG_ARCHETYPES.find((archetype) => {
    const candidate = archetype.name.toLowerCase();
    return candidate === normalized || candidate.includes(normalized) || normalized.includes(candidate);
  }) || null;
}

interface JungArchetypeWheelProps {
  selectedArchetype?: string;
  onArchetypeSelect: (archetype: string, description: string) => void;
}

export default function JungArchetypeWheel({ selectedArchetype, onArchetypeSelect }: JungArchetypeWheelProps) {
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    // Aggiorna le dimensioni quando l'immagine è caricata
    const updateDimensions = () => {
      if (imageRef.current) {
        setDimensions({
          width: imageRef.current.offsetWidth,
          height: imageRef.current.offsetHeight
        });
      }
    };

    // Aggiungi listener per il resize
    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Trova l'archetipo selezionato per posizionare il pointer
  useEffect(() => {
    if (selectedArchetype && dimensions.width > 0) {
      const archetype = JUNG_ARCHETYPES.find(a => a.name === selectedArchetype);
      if (archetype) {
        const { x, y } = calculatePosition(archetype.angle, archetype.radius);
        setPointer({ x, y });
      }
    }
  }, [selectedArchetype, dimensions]);

  const calculatePosition = (angle: number, radius: number) => {
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    const maxRadius = Math.min(centerX, centerY) * 0.8; // 80% del raggio massimo
    
    // Converti angolo in radianti (0° = destra, aumenta in senso orario)
    const rad = ((angle - 90) * Math.PI) / 180;
    
    const x = centerX + Math.cos(rad) * maxRadius * radius;
    const y = centerY + Math.sin(rad) * maxRadius * radius;
    
    return { x, y };
  };

  const findArchetypeAtPosition = (x: number, y: number): JungArchetypeData | null => {
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    
    // Calcola distanza dal centro e angolo
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const maxRadius = Math.min(centerX, centerY);
    
    // Verifica che il click sia nella zona della ruota (non troppo vicino al centro, non troppo lontano)
    const minClickRadius = maxRadius * 0.3; // Minimo 30% dal centro
    const maxClickRadius = maxRadius * 0.95; // Massimo 95% dal centro
    
    if (distance < minClickRadius || distance > maxClickRadius) {
      return null; // Click fuori dalla zona degli archetipi
    }
    
    // Calcola angolo (0° = in alto, aumenta in senso orario)
    // atan2 restituisce angolo da -π a π, con 0 a destra
    // Dobbiamo convertire per avere 0° in alto
    let angle = Math.atan2(dy, dx) * 180 / Math.PI;
    // Ruota di 90° per avere 0° in alto invece che a destra
    angle = angle + 90;
    // Normalizza tra 0 e 360
    if (angle < 0) angle += 360;
    if (angle >= 360) angle -= 360;
    
    // Trova l'archetipo più vicino angolarmente
    // Ogni archetipo copre circa 30° (360° / 12 archetipi)
    let closestArchetype: JungArchetypeData | null = null;
    let minAngleDiff = Infinity;
    
    JUNG_ARCHETYPES.forEach((archetype: JungArchetypeData) => {
      // Calcola differenza angolare (gestisce il wrap-around a 360°)
      let angleDiff = Math.abs(archetype.angle - angle);
      if (angleDiff > 180) angleDiff = 360 - angleDiff;
      
      // Ogni archetipo ha una zona di circa ±15° di tolleranza
      if (angleDiff < minAngleDiff && angleDiff < 18) { // 18° = 360° / 20 per sovrapposizione
        minAngleDiff = angleDiff;
        closestArchetype = archetype;
      }
    });
    
    return closestArchetype;
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const archetype = findArchetypeAtPosition(x, y);
    
    if (archetype) {
      setPointer({ x, y });
      onArchetypeSelect(archetype.name, archetype.description);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-700 mb-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
        <strong>💡 Come usare:</strong> Clicca su una delle 12 sezioni colorate della ruota per selezionare un archetipo.
        Evita il centro e i bordi esterni.
      </div>
      
      <div 
        ref={canvasRef}
        className="relative cursor-crosshair bg-white rounded-lg shadow-inner p-4"
        onClick={handleClick}
        style={{ maxWidth: '600px', margin: '0 auto' }}
      >
        {/* Immagine della ruota */}
        <img 
          ref={imageRef}
          src="/ruota-archetipi2-01-1.webp"
          alt="Ruota degli Archetipi di Jung"
          className="w-full h-auto rounded-lg"
          onLoad={() => {
            if (imageRef.current) {
              setDimensions({
                width: imageRef.current.offsetWidth,
                height: imageRef.current.offsetHeight
              });
            }
          }}
        />
        
        {/* Pointer per indicare la selezione */}
        {pointer && (
          <div
            className="absolute w-6 h-6 -ml-3 -mt-3 pointer-events-none transition-all duration-300"
            style={{
              left: `${pointer.x}px`,
              top: `${pointer.y}px`,
            }}
          >
            {/* Cerchio esterno pulsante */}
            <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-75"></div>
            {/* Cerchio interno fisso */}
            <div className="absolute inset-0 bg-primary rounded-full border-4 border-white shadow-lg"></div>
            {/* Punto centrale */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
        )}
        
        {/* Overlay trasparente per migliorare la visibilità del click */}
        <div className="absolute inset-0 pointer-events-none hover:bg-black hover:bg-opacity-5 rounded-lg transition-colors duration-200"></div>
      </div>
      
      {/* Legenda degli archetipi */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Archetipi disponibili:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          {JUNG_ARCHETYPES.map((archetype) => (
            <div 
              key={archetype.name}
              className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                selectedArchetype === archetype.name 
                  ? 'bg-primary text-white' 
                  : 'hover:bg-gray-200'
              }`}
              onClick={() => {
                const { x, y } = calculatePosition(archetype.angle, archetype.radius);
                setPointer({ x, y });
                onArchetypeSelect(archetype.name, archetype.description);
              }}
            >
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0" 
                style={{ backgroundColor: archetype.color }}
              ></div>
              <span className="truncate">{archetype.name}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Descrizione dell'archetipo selezionato */}
      {selectedArchetype && (
        <div className="p-4 bg-primary/10 border-l-4 border-primary rounded">
          <p className="text-sm">
            <strong className="text-primary">{selectedArchetype}:</strong>{' '}
            {JUNG_ARCHETYPES.find(a => a.name === selectedArchetype)?.description}
          </p>
        </div>
      )}
    </div>
  );
}
